import { useState } from 'react'
import {
  Box,
  Text,
  Badge,
  Table,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Portal,
} from '@chakra-ui/react'
import { CreditCard, MoreVertical, ExternalLink, XCircle } from 'lucide-react'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { useCancelSupplierPayment } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { SupplierPaymentWithSupplier, SupplierPaymentStatus, SupplierPaymentMode } from '../types'
import {
  SUPPLIER_PAYMENT_STATUS_LABELS,
  SUPPLIER_PAYMENT_STATUS_COLORS,
  SUPPLIER_PAYMENT_MODE_LABELS,
} from '../types'

interface SupplierPaymentsListProps {
  payments?: SupplierPaymentWithSupplier[]
  searchTerm: string
  onView: (payment: SupplierPaymentWithSupplier) => void
  isLoading?: boolean
}

export function SupplierPaymentsList({
  payments,
  searchTerm,
  onView,
  isLoading,
}: SupplierPaymentsListProps) {
  const cancelPayment = useCancelSupplierPayment()
  const [paymentToCancel, setPaymentToCancel] = useState<SupplierPaymentWithSupplier | null>(null)

  const filtered = payments?.filter(
    (p) =>
      (p.reference && p.reference.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.suppliers?.name && p.suppliers.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.suppliers?.code && p.suppliers.code.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleCancel = async () => {
    if (!paymentToCancel) return

    try {
      await cancelPayment.mutateAsync(paymentToCancel.id)
      toaster.create({
        title: 'Succès',
        description: 'Règlement annulé',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'annulation",
        type: 'error',
      })
    } finally {
      setPaymentToCancel(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <Text color="gray.500">Chargement des règlements...</Text>
      </Box>
    )
  }

  if (!filtered || filtered.length === 0) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <CreditCard size={48} color="var(--chakra-colors-gray-300)" style={{ margin: '0 auto' }} />
        <Text mt={4} color="gray.500">
          {searchTerm ? 'Aucun règlement trouvé' : 'Aucun règlement enregistré'}
        </Text>
        <Text fontSize="sm" color="gray.400">
          Cliquez sur "Nouveau règlement" pour en ajouter un
        </Text>
      </Box>
    )
  }

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader>Référence</Table.ColumnHeader>
              <Table.ColumnHeader>Fournisseur</Table.ColumnHeader>
              <Table.ColumnHeader>Date</Table.ColumnHeader>
              <Table.ColumnHeader>Mode</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Montant</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Statut</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" textAlign="right">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((payment) => (
              <Table.Row key={payment.id} _hover={{ bg: 'gray.50' }} cursor="pointer">
                <Table.Cell onClick={() => onView(payment)}>
                  <Text fontFamily="mono" fontWeight="medium">
                    {payment.reference || '-'}
                  </Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(payment)}>
                  <Text fontWeight="medium">{payment.suppliers?.name || '-'}</Text>
                  {payment.suppliers?.code && (
                    <Text fontSize="xs" color="gray.500">
                      {payment.suppliers.code}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(payment)}>
                  <Text fontSize="sm">{formatDate(payment.payment_date)}</Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(payment)}>
                  <Badge colorPalette="purple" size="sm">
                    {SUPPLIER_PAYMENT_MODE_LABELS[payment.payment_mode as SupplierPaymentMode]}
                  </Badge>
                </Table.Cell>
                <Table.Cell textAlign="right" onClick={() => onView(payment)}>
                  <Text fontFamily="mono" fontWeight="bold" fontSize="md">
                    {formatCurrency(payment.amount)}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center" onClick={() => onView(payment)}>
                  <Badge
                    colorPalette={SUPPLIER_PAYMENT_STATUS_COLORS[payment.status as SupplierPaymentStatus] || 'gray'}
                    size="sm"
                  >
                    {SUPPLIER_PAYMENT_STATUS_LABELS[payment.status as SupplierPaymentStatus]}
                  </Badge>
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <MenuRoot positioning={{ placement: 'bottom-end' }}>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="160px">
                          <MenuItem value="view" onClick={() => onView(payment)}>
                            <ExternalLink size={16} />
                            <Text ml={2}>Voir le détail</Text>
                          </MenuItem>

                          {payment.status === 'PENDING' && (
                            <MenuItem
                              value="cancel"
                              color="red.500"
                              onClick={() => setPaymentToCancel(payment)}
                            >
                              <XCircle size={16} />
                              <Text ml={2}>Annuler</Text>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Confirmation d'annulation */}
      <ConfirmDialog
        isOpen={!!paymentToCancel}
        onClose={() => setPaymentToCancel(null)}
        onConfirm={handleCancel}
        title="Annuler le règlement"
        message={`Êtes-vous sûr de vouloir annuler ce règlement de ${paymentToCancel ? formatCurrency(paymentToCancel.amount) : ''} ? Les factures associées seront remises en statut "Non payé".`}
        confirmLabel="Annuler le règlement"
        colorPalette="red"
        icon={<XCircle size={20} />}
        isLoading={cancelPayment.isPending}
      />
    </>
  )
}
